import { bindable, customElement, resolve } from 'aurelia';
import { I18N } from '@aurelia/i18n';
import template from './document-modal.html';
import type { Document, DocumentFile, FollowUp, FollowUpHistoryEntry, FormDefinition, SharedUser, Tag, RuleApplicationResponse } from '../services/api.types';
import { SECURITY_LEVELS } from '../services/api.types';
import { createDocument, updateDocument, uploadFile, removeFile, reorderFiles, ocrDocumentFile, addFollowUp, removeFollowUp, updateFollowUp, getFollowUpHistory, getFollowUpHistoryAvailability, clearIncomingFlag, applyRulesToDocument, shareDocuments, unshareDocument, getDocumentShares, getMe, deleteDocument, formatFileSize as formatFileSizeShared, listForms, readDocumentFileText, addImportProtocolEntry, createImportProtocol, completeImportProtocol } from '../services/api.service';
import { ConfirmDialog, type DuplicateResultItem } from './confirm-dialog';
import { apiUrl } from '../services/runtime-config';
import type { UserInput } from './user-input';
import {
  autoTagNamesForFormData,
  formPreviewDetails as getFormPreviewDetails,
  isFormSubmissionFile as isFormSubmissionFileShared,
  parseFormSubmission,
} from '../services/form-submission.service';
import type { FormPreviewDetails } from '../services/form-submission.service';

type PendingPreviewEntry = {
  file: File;
  objectUrl: string | null;
  previewKind: 'image' | 'pdf' | null;
};

type SidebarPreviewItem = {
  key: string;
  fileName: string;
  isPending: boolean;
  hasPreview: boolean;
  previewKind: 'image' | 'pdf' | null;
  previewUrl?: string;
  fileId?: string;
  existingIndex?: number;
  existingFile?: DocumentFile;
  uploadedAt?: string;
  pendingIndex?: number;
  isFormSubmission?: boolean;
  formPreviewTitle?: string;
  formPreviewUniqueKey?: string;
  markedForRemoval: boolean;
  canEdit: boolean;
  canOcr?: boolean;
  hasEmbeddedText?: boolean;
  fileSizeBytes?: number | null;
  pageCount?: number | null;
};

type DocumentModalTab = 'details' | 'followUpHistory';

type FollowUpHistoryGroup = {
  followUpId: string;
  latest: FollowUpHistoryEntry;
  previous: FollowUpHistoryEntry[];
};

/** A history entry augmented with flags indicating which fields changed compared to the next-older entry. */
type FollowUpHistoryViewEntry = FollowUpHistoryEntry & {
  isInitial: boolean;
  changedDueDate: boolean;
  changedForUser: boolean;
  changedNote: boolean;
  hasVisibleChanges: boolean;
};

@customElement({ name: 'document-modal', template })
export class DocumentModal {
  private readonly i18n: I18N = resolve(I18N);
  @bindable public saved!: () => void;
  @bindable public closed!: () => void;
  /** Called after the user confirms deleting the document being edited. */
  @bindable public deleted: (() => void) | null = null;
  /** When provided the modal operates in edit mode */
  @bindable public documentToEdit: Document | null = null;
  /** When true, show the "Mark as reviewed" button (incoming documents view) */
  @bindable public isIncoming = false;
  /** Files pre-populated via drag-and-drop from outside the UI */
  @bindable public initialFiles: File[] = [];
  /** Import protocol created for the external drag-and-drop batch, if any. */
  @bindable public importProtocolNumber: number | null = null;
  @bindable public currentUsername = '';

  public title = '';
  public description = '';
  public documentDate = new Date().toISOString().substring(0, 10);
  public securityLevel = 1;
  public readonly securityLevels = SECURITY_LEVELS;
  /** Existing files shown in edit mode */
  public existingFiles: DocumentFile[] = [];
  public ocrInProgress: Record<string, boolean> = {};
  public ocrRunningCount = 0;
  /** IDs of existing files the user wants to remove */
  public filesToRemove: string[] = [];
  /** New files queued for upload */
  public pendingFiles: File[] = [];
  public selectedTags: Tag[] = [];
  public sharedWith: SharedUser[] = [];
  private _originalSharedWith: SharedUser[] = [];
  /** Existing follow-ups (edit mode) */
  public existingFollowUps: FollowUp[] = [];
  /** Original snapshot of follow-ups for dirty detection */
  private _originalFollowUps: FollowUp[] = [];
  /** Follow-up IDs to delete on save */
  public followUpsToRemove: string[] = [];
  /** New follow-ups to add on save */
  public pendingFollowUps: { dueDate: string; note: string; forUser: string | null }[] = [];
  /** Id of an existing (already-saved) follow-up currently being edited via the follow-up modal (null = not editing a saved one) */
  public editingFollowUpId: string | null = null;
  /** Index of a pending (not-yet-saved) follow-up currently being edited via the follow-up modal (null = not editing a pending one) */
  public editingPendingFollowUpIndex: number | null = null;
  /** Form fields shared by the add/edit follow-up modal */
  public newFollowUpDate = '';
  public newFollowUpNote = '';
  public followUpForUser = '';
  public saving = false;
  public deleting = false;
  public error = '';
  public shareInputRef: UserInput | null = null;

  /** Tracks the initial file order (by ID) for dirty detection */
  private _initialFileOrder: string[] = [];

  /** Incoming: pending decision to mark as reviewed (applied on save) */
  public pendingMarkAsReviewed = false;

  /** Drag-and-drop state for preview sidebar reordering */
  public dragSourceIndex: number | null = null;
  public dragOverIndex: number | null = null;

  /** File drag-and-drop from OS into the modal */
  public isFileDragOver = false;
  private _modalDragEl: HTMLElement | null = null;
  public modalElement: HTMLElement | null = null;
  private ownsImportProtocol = false;

  /** Lightbox state for incoming preview */
  public lightboxFile: DocumentFile | null = null;
  public lightboxIndex = 0;
  /** Image editor state for preview thumbnails */
  public editingImageFile: DocumentFile | null = null;

  /** Side-by-side mode: fullscreen edit form + persistent file viewer */
  public sideBySideMode = false;
  public sideBySideFileId: string | null = null;

  /** Move/merge-files-into-another-document dialog state */
  public showMoveDialog = false;
  public moveDialogPreselectedIds: string[] = [];
  public moveSuccessMessage = '';

  /** Share dialog (edit mode) / inline share editor (create mode) state */
  public showShareModal = false;
  public showShareEditor = false;

  /** Add-follow-up editor reveal state */
  public showFollowUpEditor = false;

  public activeTab: DocumentModalTab = 'details';
  public followUpHistoryAvailable = false;
  public followUpHistoryCount = 0;
  public followUpHistoryLoading = false;
  public followUpHistoryLoaded = false;
  public followUpHistoryError = '';
  public followUpHistory: FollowUpHistoryEntry[] = [];
  public expandedFollowUpHistory: Record<string, boolean> = {};
  public formPreviewDetails: Record<string, FormPreviewDetails> = {};

  public get sideBySideFile(): DocumentFile | null {
    if (!this.sideBySideFileId) return null;
    return this.existingFiles.find(f => f.id === this.sideBySideFileId) ?? null;
  }

  private fileInputRef!: HTMLInputElement;
  private pendingPreviewEntries: PendingPreviewEntry[] = [];
  private _initialTitle = '';
  private _initialDescription = '';
  private _initialDate = '';
  private _keyHandler!: (e: KeyboardEvent) => void;
  /** Last measured natural height of the details tab, used to pin the dialog height while the history tab is shown. */
  private _detailsHeightPx: number | null = null;
  private _formPreviewLoadVersion = 0;
  private readonly _modalResizeHandler = (): void => {
    if (this.activeTab === 'details') {
      this.scheduleDetailsModalHeightCapture();
    }
  };

  public get isEditMode(): boolean {
    return this.documentToEdit !== null;
  }

  public get hasChanges(): boolean {
    if (this.title !== this._initialTitle) return true;
    if (this.description !== this._initialDescription) return true;
    if (this.documentDate !== this._initialDate) return true;
    if (!this._sameUsers(this.sharedWith, this._originalSharedWith)) return true;
    if (this.filesToRemove.length > 0) return true;
    if (this.pendingFiles.length > 0) return true;
    if (this.followUpsToRemove.length > 0) return true;
    if (this.pendingFollowUps.length > 0) return true;
    if (this.pendingMarkAsReviewed) return true;
    const currentOrder = this.existingFiles.map(f => f.id);
    if (currentOrder.length !== this._initialFileOrder.length ||
        currentOrder.some((id, i) => id !== this._initialFileOrder[i])) return true;
    const hasModifiedFollowUp = this.existingFollowUps.some(fu => {
      const orig = this._originalFollowUps.find(f => f.id === fu.id);
      return orig && (
        fu.dueDate !== orig.dueDate ||
        fu.note !== orig.note ||
        fu.forUser !== orig.forUser
      );
    });
    if (hasModifiedFollowUp) return true;
    return false;
  }

  public binding(): void {
    this.reset();
    if (this.documentToEdit) {
      this.title        = this.documentToEdit.title;
      this.description  = this.documentToEdit.description;
      this.documentDate = this.documentToEdit.documentDate.substring(0, 10);
      this.securityLevel = this.documentToEdit.securityLevel ?? 1;
      this.existingFiles = [...this.documentToEdit.files];
      void this.loadFormPreviewDetails();
      this.selectedTags = this.documentToEdit.tags ? [...this.documentToEdit.tags] : [];
      this.sharedWith = this.documentToEdit.sharedWith ? this.documentToEdit.sharedWith.map(user => ({ ...user })) : [];
      this._originalSharedWith = this.documentToEdit.sharedWith ? this.documentToEdit.sharedWith.map(user => ({ ...user })) : [];
      this.existingFollowUps = this.documentToEdit.followUps
        ? this.documentToEdit.followUps.map(fu => ({ ...fu }))
        : [];
      this._originalFollowUps = this.documentToEdit.followUps
        ? this.documentToEdit.followUps.map(fu => ({ ...fu }))
        : [];
      this._initialFileOrder = this.existingFiles.map(f => f.id);
    }
    this._initialTitle       = this.title;
    this._initialDescription = this.description;
    this._initialDate        = this.documentDate;
  }

  /**
   * Aurelia bindable change callback: if this component instance is reused for a different
   * document (e.g. switching the document being edited while the dialog stays mounted),
   * fully re-initialize the form and reset back to the details tab / fresh history state.
   */
  public documentToEditChanged(newDoc: Document | null, oldDoc: Document | null): void {
    if (!oldDoc || !newDoc || newDoc.id === oldDoc.id) return;
    this.binding();
    if (this.isEditMode) {
      void this.loadFollowUpHistoryAvailability();
    }
  }

  public bound(): void {
    if (this.initialFiles?.length > 0) {
      this._addFiles(this.initialFiles);
    }
    if (this.isEditMode) {
      void this.loadFollowUpHistoryAvailability();
    }
  }

  public async selectTab(tab: DocumentModalTab): Promise<void> {
    if (tab === this.activeTab) return;
    if (tab === 'followUpHistory' && !this.followUpHistoryAvailable) return;
    if (tab === 'followUpHistory' && this.activeTab === 'details') {
      this.captureDetailsModalHeight();
    }
    this.activeTab = tab;
    if (tab === 'followUpHistory') {
      if (this.modalElement && this._detailsHeightPx) {
        this.modalElement.style.height = `${this._detailsHeightPx}px`;
      }
      this.sideBySideMode = false;
      await this.loadFollowUpHistory();
    } else {
      if (this.modalElement) {
        this.modalElement.style.height = '';
      }
      this.scheduleDetailsModalHeightCapture();
    }
  }

  public get followUpHistoryGroups(): FollowUpHistoryGroup[] {
    const groups = new Map<string, FollowUpHistoryGroup>();
    for (const entry of this.followUpHistory) {
      const group = groups.get(entry.followUpId);
      if (!group) {
        groups.set(entry.followUpId, {
          followUpId: entry.followUpId,
          latest: entry,
          previous: [],
        });
      } else {
        group.previous.push(entry);
      }
    }

    return [...groups.values()].sort((a, b) =>
      this.historyTimestamp(b.latest.occurredAt) - this.historyTimestamp(a.latest.occurredAt));
  }

  public historyEntriesForGroup(group: FollowUpHistoryGroup): FollowUpHistoryViewEntry[] {
    const entries = [group.latest, ...group.previous];
    return entries.map((entry, index) => {
      const previous = entries[index + 1] ?? null;
      const changedDueDate = !previous || entry.dueDate !== previous.dueDate;
      const changedForUser = !previous || entry.forUserId !== previous.forUserId;
      const changedNote = !previous || (entry.note ?? '') !== (previous.note ?? '');
      return {
        ...entry,
        isInitial: !previous,
        changedDueDate,
        changedForUser,
        changedNote,
        hasVisibleChanges: changedDueDate || changedForUser || (changedNote && !!entry.note),
      };
    });
  }

  public followUpHistoryLabel(group: FollowUpHistoryGroup): string {
    const note = group.latest.note?.trim();
    if (note) return note;
    return `${this.i18n.tr('documents.form.followUpHistory.followUp')} ${group.followUpId.substring(0, 8)}`;
  }

  public followUpHistoryToggleLabel(group: FollowUpHistoryGroup, _expanded?: Record<string, boolean>): string {
    return this.i18n.tr(
      this.isFollowUpHistoryExpanded(group.followUpId)
        ? 'documents.form.followUpHistory.hideEarlier'
        : 'documents.form.followUpHistory.showEarlier',
    );
  }

  // The second parameter is unused by the method body, but passing the
  // `expandedFollowUpHistory` property from the template makes Aurelia's
  // binding observer track it, so the view re-evaluates when it changes.
  public isFollowUpHistoryExpanded(followUpId: string, _expanded?: Record<string, boolean>): boolean {
    return this.expandedFollowUpHistory[followUpId] === true;
  }

  public toggleFollowUpHistory(followUpId: string): void {
    this.expandedFollowUpHistory = {
      ...this.expandedFollowUpHistory,
      [followUpId]: !this.isFollowUpHistoryExpanded(followUpId),
    };
  }

  public historyActionLabel(action: string): string {
    const key = action === 'created'
      ? 'documents.form.followUpHistory.actions.created'
      : action === 'completed'
        ? 'documents.form.followUpHistory.actions.completed'
        : 'documents.form.followUpHistory.actions.updated';
    return this.i18n.tr(key);
  }

  public historyActionClass(action: string): string {
    return action === 'created' || action === 'completed' ? action : 'updated';
  }

  public historyEntryUser(entry: FollowUpHistoryEntry): string {
    return entry.forDisplayName || entry.forUsername || String(entry.forUserId);
  }

  private historyTimestamp(value: string): number {
    const timestamp = Date.parse(value);
    return Number.isNaN(timestamp) ? 0 : timestamp;
  }

  private async loadFollowUpHistoryAvailability(): Promise<void> {
    if (!this.documentToEdit) return;
    this.followUpHistoryError = '';
    try {
      const availability = await getFollowUpHistoryAvailability(this.documentToEdit.id);
      this.followUpHistoryCount = availability.count;
      this.followUpHistoryAvailable = availability.hasHistory;
    } catch (err: unknown) {
      this.followUpHistoryAvailable = false;
      this.followUpHistoryError = err instanceof Error
        ? err.message
        : this.i18n.tr('documents.form.followUpHistory.loadFailed');
    }
  }

  private async loadFollowUpHistory(): Promise<void> {
    if (!this.documentToEdit || this.followUpHistoryLoaded || this.followUpHistoryLoading) return;
    this.followUpHistoryLoading = true;
    this.followUpHistoryError = '';
    try {
      this.followUpHistory = await getFollowUpHistory(this.documentToEdit.id);
      this.followUpHistoryLoaded = true;
    } catch (err: unknown) {
      this.followUpHistoryError = err instanceof Error
        ? err.message
        : this.i18n.tr('documents.form.followUpHistory.loadFailed');
    } finally {
      this.followUpHistoryLoading = false;
    }
  }

  private scheduleDetailsModalHeightCapture(): void {
    if (!this.modalElement || this.activeTab !== 'details') return;
    requestAnimationFrame(() => {
      if (this.activeTab === 'details') {
        this.captureDetailsModalHeight();
      }
    });
  }

  private captureDetailsModalHeight(): void {
    if (!this.modalElement) return;
    // Only records the natural details-tab height; does not pin it onto the
    // element, so the dialog never gets an inline height while details is shown
    // (which previously caused a stray scrollbar on first open).
    const previousInlineHeight = this.modalElement.style.height;
    this.modalElement.style.height = 'auto';
    const measuredHeight = Math.ceil(this.modalElement.getBoundingClientRect().height);
    this.modalElement.style.height = previousInlineHeight;
    if (measuredHeight > 0) {
      this._detailsHeightPx = measuredHeight;
    }
  }

  /** Called by Aurelia when initialFiles binding changes after initial binding */
  public initialFilesChanged(newFiles: File[]): void {
    if (newFiles?.length > 0 && this.pendingFiles.length === 0) {
      this._addFiles(newFiles);
    }
  }

  public attached(): void {
    if (!this.currentUsername) {
      void this._loadCurrentUser();
    }
    this._keyHandler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (this.lightboxFile) {
          this.closeLightbox();
        } else if (this.showMoveDialog) {
          // handled by move-files-dialog's own Escape listener
        } else if (this.showShareModal) {
          this.closeShareModal();
        } else if (this.showFollowUpEditor) {
          this.cancelAddFollowUp();
        } else if (this.sideBySideMode) {
          this.sideBySideMode = false;
        } else {
          void this.cancel();
        }
      }
      if (this.lightboxFile) {
        if (e.key === 'ArrowLeft')  this.lightboxPrev();
        if (e.key === 'ArrowRight') this.lightboxNext();
      }
    };
    document.addEventListener('keydown', this._keyHandler);
    window.addEventListener('resize', this._modalResizeHandler);
    this.scheduleDetailsModalHeightCapture();
  }

  public detached(): void {
    document.removeEventListener('keydown', this._keyHandler);
    window.removeEventListener('resize', this._modalResizeHandler);
    this.isFileDragOver = false;
    this._modalDragEl = null;
    this.modalElement = null;
    this.clearPendingPreviewEntries();
  }

  public openFilePicker(): void {
    this.fileInputRef.value = '';
    this.fileInputRef.click();
  }

  public onFilesAdded(event: Event): void {
    const input = event.target as HTMLInputElement;
    const files = input.files ? Array.from(input.files) : [];
    this._addFiles(files);
  }

  private _addFiles(files: File[]): void {
    const existing = new Set(this.pendingFiles.map(f => `${f.name}|${f.size}`));
    const unique = files.filter(f => !existing.has(`${f.name}|${f.size}`));
    const previewEntries = unique.map(file => ({
      file,
      objectUrl: this.canPreviewPendingFile(file) ? URL.createObjectURL(file) : null,
      previewKind: this.previewKindForPendingFile(file),
    }));
    const combined = [
      ...this.pendingFiles.map((file, index) => ({
        file,
        previewEntry: this.pendingPreviewEntries[index],
      })),
      ...previewEntries.map(previewEntry => ({
        file: previewEntry.file,
        previewEntry,
      })),
    ].sort((left, right) => this.compareFileNames(left.file, right.file));

    this.pendingFiles = combined.map(item => item.file);
    this.pendingPreviewEntries = combined.map(item => item.previewEntry);
  }

  private compareFileNames(left: File, right: File): number {
    return left.name.localeCompare(
      right.name,
      undefined,
      { numeric: true, sensitivity: 'base' },
    );
  }

  // ── OS drag-and-drop into modal ───────────────────────────────────────────

  public onModalDragEnter(e: DragEvent): void {
    if (e.dataTransfer?.types.includes(DocumentModal.REORDER_TYPE)) return;
    if (this.hasExternalDragPayload(e)) {
      this.isFileDragOver = true;
      if (!this._modalDragEl) this._modalDragEl = e.currentTarget as HTMLElement;
    }
  }

  public onModalDragLeave(e: DragEvent): void {
    const related = e.relatedTarget as Node | null;
    if (related && this._modalDragEl?.contains(related)) return;
    this.isFileDragOver = false;
  }

  public onModalDragOver(e: DragEvent): void {
    if (e.dataTransfer?.types.includes(DocumentModal.REORDER_TYPE)) return;
    if (this.hasExternalDragPayload(e) || this.isFileDragOver) {
      e.preventDefault();
      if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
    }
  }

  public async onModalDrop(e: DragEvent): Promise<void> {
    e.preventDefault();
    this.isFileDragOver = false;
    // Ignore drops that originated from the internal reorder drag.
    if (e.dataTransfer?.types.includes(DocumentModal.REORDER_TYPE)) return;
    const files = e.dataTransfer?.files ? Array.from(e.dataTransfer.files) : [];
    if (files.length === 0) return;
    if (!await this.ensureImportProtocol()) return;
    this._addFiles(files);
  }

  private hasExternalDragPayload(e: DragEvent): boolean {
    const dataTransfer = e.dataTransfer;
    if (!dataTransfer) return false;
    return !Array.from(dataTransfer.types)
      .some(type => type.toLowerCase() === DocumentModal.REORDER_TYPE);
  }

  public removePending(index: number): void {
    this.revokePendingPreview(index);
    this.pendingFiles = this.pendingFiles.filter((_, i) => i !== index);
    this.pendingPreviewEntries = this.pendingPreviewEntries.filter((_, i) => i !== index);
  }

  public toggleRemoveExisting(fileId: string): void {
    if (this.filesToRemove.includes(fileId)) {
      this.filesToRemove = this.filesToRemove.filter(id => id !== fileId);
    } else {
      this.filesToRemove = [...this.filesToRemove, fileId];
    }
  }

  public moveFileUp(index: number): void {
    if (index <= 0) return;
    const files = [...this.existingFiles];
    [files[index - 1], files[index]] = [files[index], files[index - 1]];
    this.existingFiles = files;
  }

  public moveFileDown(index: number): void {
    if (index >= this.existingFiles.length - 1) return;
    const files = [...this.existingFiles];
    [files[index], files[index + 1]] = [files[index + 1], files[index]];
    this.existingFiles = files;
  }

  // ── Preview pane drag-and-drop reordering ─────────────────────────────────

  /** Sentinel type set on all internal reorder drags so OS-file handlers can ignore them. */
  private static readonly REORDER_TYPE = 'application/x-radish-reorder';

  public onDragStart(e: DragEvent, existingIndex: number | undefined): void {
    if (existingIndex === undefined) return;
    this.dragSourceIndex = existingIndex;
    // clearData removes any browser-added image/URL payload that would confuse
    // the OS-file drop handler; the sentinel type identifies this as an internal drag.
    e.dataTransfer!.clearData();
    e.dataTransfer!.setData(DocumentModal.REORDER_TYPE, String(existingIndex));
    e.dataTransfer!.effectAllowed = 'move';
  }

  public onDragOver(e: DragEvent, existingIndex: number | undefined): void {
    if (existingIndex === undefined || this.dragSourceIndex === null) return;
    e.preventDefault();
    e.stopPropagation(); // don't let the modal's drag-over handler interfere
    e.dataTransfer!.dropEffect = 'move';
    this.dragOverIndex = existingIndex;
  }

  public onDrop(e: DragEvent, existingIndex: number | undefined): void {
    e.preventDefault();
    e.stopPropagation(); // prevent bubbling to onModalDrop
    const src = this.dragSourceIndex;
    this.dragSourceIndex = null;
    this.dragOverIndex = null;
    if (existingIndex === undefined || src === null || src === existingIndex) return;
    const files = [...this.existingFiles];
    const [moved] = files.splice(src, 1);
    files.splice(existingIndex, 0, moved);
    this.existingFiles = files;
  }

  public onDragEnd(): void {
    this.dragSourceIndex = null;
    this.dragOverIndex = null;
  }

  public openImageEditor = (file: DocumentFile): void => {
    this.editingImageFile = file;
  };

  public closeImageEditor = (): void => {
    this.editingImageFile = null;
  };

  public onImageSaved = (updatedFile: DocumentFile): void => {
    this.existingFiles = this.existingFiles.map(file => file.id === updatedFile.id ? updatedFile : file);
    if (this.documentToEdit) {
      this.documentToEdit.files = this.documentToEdit.files.map(file => file.id === updatedFile.id ? updatedFile : file);
    }
    this.editingImageFile = null;
  };

  public async runOcr(file: DocumentFile): Promise<void> {
    if (this.ocrInProgress[file.id]) return;
    this.ocrInProgress = { ...this.ocrInProgress, [file.id]: true };
    this.ocrRunningCount++;
    this.error = '';
    try {
      const updated = await ocrDocumentFile(this.documentToEdit!.id, file.id);
      this.existingFiles = this.existingFiles.map(f => f.id === updated.id ? updated : f);
      if (this.documentToEdit) {
        this.documentToEdit.files = this.documentToEdit.files.map(f => f.id === updated.id ? updated : f);
      }
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e);
      this.error = msg.includes('not available')
        ? msg
        : this.i18n.tr('documents.ocr.failed');
      console.error('OCR failed', e);
    } finally {
      const { [file.id]: _, ...rest } = this.ocrInProgress;
      this.ocrInProgress = rest;
      this.ocrRunningCount--;
    }
  }

  public get sidebarFiles(): SidebarPreviewItem[] {
    const existing = this.existingFiles.map((file, index) => ({
      key: file.id,
      fileName: file.fileName,
      isPending: false,
      hasPreview: file.hasPreview,
      previewKind: this.previewKindForExistingFile(file),
      previewUrl: file.hasPreview ? this.previewUrl(file.id, file.uploadedAt) : undefined,
      fileId: file.id,
      existingIndex: index,
      existingFile: file,
      uploadedAt: file.uploadedAt,
      isFormSubmission: this.isFormSubmissionFile(file),
      formPreviewTitle: this.isFormSubmissionFile(file) ? this.formPreviewTitle(file) : undefined,
      formPreviewUniqueKey: this.isFormSubmissionFile(file) ? this.formPreviewUniqueKey(file) : undefined,
      markedForRemoval: this.filesToRemove.includes(file.id),
      canEdit: this.isImage(file),
      canOcr: this.isPdf(file) && !file.hasEmbeddedText,
      hasEmbeddedText: file.hasEmbeddedText ?? false,
      fileSizeBytes: file.fileSizeBytes,
      pageCount: file.pageCount,
    }));

    const pending = this.pendingFiles.map((file, index) => {
      const entry = this.pendingPreviewEntries[index];
      const previewUrl = entry?.objectUrl ?? undefined;
      return {
        key: `pending-${index}`,
        fileName: file.name,
        isPending: true,
        hasPreview: !!previewUrl,
        previewKind: entry?.previewKind ?? null,
        previewUrl,
        pendingIndex: index,
        markedForRemoval: false,
        canEdit: false,
        canOcr: false,
        hasEmbeddedText: false,
        fileSizeBytes: file.size,
      };
    });

    return [...existing, ...pending];
  }

  /** Total known size of all files currently shown in the sidebar (existing + pending),
   *  mirroring the summary shown in document-view. */
  public get totalSidebarFileSizeLabel(): string {
    const knownSizes = this.sidebarFiles
      .map(item => item.fileSizeBytes)
      .filter((size): size is number => size != null);
    if (knownSizes.length === 0) return '';
    const total = knownSizes.reduce((sum, size) => sum + size, 0);
    return this.formatFileSize(total);
  }

  public isMarkedForRemoval(fileId: string): boolean {
    return this.filesToRemove.includes(fileId);
  }

  public toggleSidebarRemoval(item: SidebarPreviewItem): void {
    if (item.isPending) {
      return;
    }
    this.toggleRemoveExisting(item.fileId!);
  }

  public removePendingSidebarItem(index: number): void {
    this.removePending(index);
  }

  // ── Follow-Up management ─────────────────────────────────────────────────

  public addPendingFollowUp(): void {
    if (!this.newFollowUpDate) return;
    this.pendingFollowUps = [...this.pendingFollowUps, {
      dueDate: this.newFollowUpDate,
      note: this.newFollowUpNote.trim(),
      forUser: this.followUpForUser || null,
    }];
    this.newFollowUpDate = '';
    this.newFollowUpNote = '';
    this.followUpForUser = '';
  }

  public removePendingFollowUp(index: number): void {
    this.pendingFollowUps = this.pendingFollowUps.filter((_, i) => i !== index);
  }

  public toggleRemoveFollowUp(followUpId: string): void {
    if (this.followUpsToRemove.includes(followUpId)) {
      this.followUpsToRemove = this.followUpsToRemove.filter(id => id !== followUpId);
    } else {
      this.followUpsToRemove = [...this.followUpsToRemove, followUpId];
    }
  }

  public isFollowUpMarkedForRemoval(followUpId: string): boolean {
    return this.followUpsToRemove.includes(followUpId);
  }

  public startEditFollowUp(fu: FollowUp): void {
    this.editingFollowUpId = fu.id;
    this.editingPendingFollowUpIndex = null;
    this.newFollowUpDate   = fu.dueDate.substring(0, 10);
    this.newFollowUpNote   = fu.note ?? '';
    this.followUpForUser   = fu.forUser ?? this.currentUsername;
    this.showFollowUpEditor = true;
  }

  public startEditPendingFollowUp(index: number): void {
    const fu = this.pendingFollowUps[index];
    if (!fu) return;
    this.editingPendingFollowUpIndex = index;
    this.editingFollowUpId = null;
    this.newFollowUpDate   = fu.dueDate;
    this.newFollowUpNote   = fu.note;
    this.followUpForUser   = fu.forUser ?? '';
    this.showFollowUpEditor = true;
  }

  public get isEditingFollowUp(): boolean {
    return this.editingFollowUpId !== null || this.editingPendingFollowUpIndex !== null;
  }

  public get isEditingSavedFollowUp(): boolean {
    return this.editingFollowUpId !== null;
  }

  public get followUpModalTitle(): string {
    return this.isEditingFollowUp
      ? this.i18n.tr('documents.form.editFollowUp')
      : this.i18n.tr('documents.form.addFollowUp');
  }

  public get followUpAssignableUsers(): SharedUser[] {
    return this.sharedWith ?? [];
  }

  public get followUpAssignToMeLabel(): string {
    return this.i18n.tr('documents.form.assignToMe');
  }

  public displayName(username?: string | null, name?: string | null): string {
    return name || username || '';
  }

  public followUpForLabel(username?: string | null, name?: string | null): string {
    const label = this.displayName(username, name);
    return label ? `${this.i18n.tr('documents.form.reminderFor')}${label}` : '';
  }

  public isFollowUpForOtherUser(fu: { forUser?: string | null }): boolean {
    const currentUsername = this.currentUsername || this.documentToEdit?.owner || '';
    return !!fu.forUser && fu.forUser.toLowerCase() !== currentUsername.toLowerCase();
  }

  public focusShareInput(): void {
    this.shareInputRef?.focusInput();
  }

  public clearTitle(): void {
    this.title = '';
  }

  public clearDescription(): void {
    this.description = '';
  }

  public async save(): Promise<void> {
    const manuallyEnteredTitle = this.title.trim();
    const fallbackTitle = !this.isEditMode && this.pendingFiles.length > 0
      ? this.pendingFiles[0].name.replace(/\.[^/.]+$/, '') || this.pendingFiles[0].name
      : '';
    const effectiveTitle = manuallyEnteredTitle || fallbackTitle;
    if (!effectiveTitle) {
      this.error = this.i18n.tr('documents.form.titleRequired');
      return;
    }
    this.saving = true;
    this.error = '';
    const filesToUpload = [...this.pendingFiles]
      .sort((left, right) => this.compareFileNames(left, right));
    const protocolFiles: {
      file: File;
      startedAt: string;
      uploaded: DocumentFile;
    }[] = [];
    let createdDocumentId: string | null = null;
    let failedUpload: {
      file: File;
      startedAt: string;
      error: string;
    } | null = null;
    let protocolEntriesRecorded = false;
    try {
      let docId: string;

      if (this.isEditMode) {
          await this.removeOrphanedFormTags();
      }

      const tagNames = this.selectedTags.map(t => t.name);

      if (this.isEditMode) {
          const updated = await updateDocument(this.documentToEdit!.id, {
            title: this.title.trim(),
            description: this.description.trim(),
            documentDate: new Date(this.documentDate).toISOString(),
            tagNames,
            securityLevel: this.securityLevel,
          });
          docId = updated.id;
          createdDocumentId = docId;

          for (const fileId of this.filesToRemove) {
            await removeFile(docId, fileId);
          }

          // Reorder files if order changed (excluding removed files)
          const remainingIds = this.existingFiles
            .filter(f => !this.filesToRemove.includes(f.id))
            .map(f => f.id);
          const initialRemaining = this._initialFileOrder.filter(id => !this.filesToRemove.includes(id));
          const orderChanged = remainingIds.length !== initialRemaining.length ||
            remainingIds.some((id, i) => id !== initialRemaining[i]);
          if (orderChanged && remainingIds.length > 0) {
            await reorderFiles(docId, remainingIds);
          }

          for (const followUpId of this.followUpsToRemove) {
            await removeFollowUp(docId, followUpId);
          }

          // Update modified existing follow-ups
          for (const fu of this.existingFollowUps) {
            if (!this.followUpsToRemove.includes(fu.id)) {
              const original = this._originalFollowUps.find(f => f.id === fu.id);
                      if (original && (
                        fu.dueDate !== original.dueDate ||
                        fu.note !== original.note ||
                        fu.forUser !== original.forUser
                      )) {
                        await updateFollowUp(docId, fu.id, {
                          dueDate: new Date(fu.dueDate).toISOString(),
                          note: fu.note ?? undefined,
                          forUsername: (fu.forUser ?? this.currentUsername) || undefined,
                        });
                      }
            }
          }
      } else {
          const created = await createDocument({
            title: effectiveTitle,
            description: this.description.trim(),
            documentDate: new Date(this.documentDate).toISOString(),
            tagNames,
            securityLevel: this.securityLevel,
          });
          docId = created.id;
          createdDocumentId = docId;
      }

      const duplicateResults: DuplicateResultItem[] = [];
      for (const file of filesToUpload) {
          const startedAt = new Date().toISOString();
          try {
            const uploaded = await uploadFile(docId, file);
            protocolFiles.push({ file, startedAt, uploaded });
            if (uploaded.isDuplicate) {
              const status = uploaded.duplicateSkipped ? 'rejected' : 'added';
              const statusKey = uploaded.duplicateSkipped
                ? 'documents.form.duplicateFileRejectedStatus'
                : this.isEditMode
                  ? 'documents.form.duplicateFileAddedToExistingDocumentStatus'
                  : 'documents.form.duplicateFileAddedToNewDocumentStatus';
              const detailKey = uploaded.duplicateSkipped
                ? 'documents.form.duplicateFileSkippedWarning'
                : 'documents.form.duplicateFileAddedDetail';
              duplicateResults.push({
                fileName: uploaded.fileName,
                status,
                statusText: this.i18n.tr(statusKey),
                detail: this.i18n.tr(detailKey, {
                  fileName: uploaded.fileName,
                  duplicateFileName: uploaded.duplicateFileName ?? uploaded.fileName,
                  documentTitle: uploaded.duplicateDocumentTitle ?? '',
                }),
              });
            }
          } catch (error: unknown) {
            failedUpload = {
              file,
              startedAt,
              error: error instanceof Error ? error.message : String(error),
            };
            throw error;
          }
      }
      const sourceDocumentId = docId;
      const sourceDocumentTitle = this.title.trim() || effectiveTitle;
      let ruleResponse: RuleApplicationResponse | null = null;
      let ruleError: string | null = null;
      let sourceDocumentDeletedByClient = false;
      if (!this.isEditMode && this.pendingFiles.length > 0) {
        ruleResponse = await applyRulesToDocument(docId, manuallyEnteredTitle.length > 0)
          .then(response => {
            console.info('[Radish DMS] Automatic rule result', {
              documentId: docId,
              ...response,
            });
            if (response.error) {
              console.warn('[Radish DMS] Automatic rule reported an error', {
                documentId: docId,
                error: response.error,
              });
            }
            if (response.targetDocumentId) {
              docId = response.targetDocumentId;
            }
            return response;
          })
          .catch((err: unknown) => {
            ruleError = err instanceof Error ? err.message : String(err);
            console.warn('Server-side rule application failed:', err);
            return null;
          });
      }
      for (const protocolFile of protocolFiles) {
        const ruleDuplicateRejected = ruleResponse?.duplicateSkipped === true;
        const duplicateRejected = protocolFile.uploaded.duplicateSkipped === true ||
          ruleDuplicateRejected;
        const moved = ruleResponse?.fileMoved === true && !!ruleResponse.targetDocumentId;
        const outcome = duplicateRejected
          ? 'rejected'
          : ruleError || ruleResponse?.error
            ? 'error'
            : moved
              ? 'moved'
              : 'added';
        const currentDocumentId = ruleDuplicateRejected
          ? null
          : moved
            ? ruleResponse?.targetDocumentId
            : sourceDocumentId;
        const currentDocumentTitle = moved
          ? ruleResponse?.documentTitle
          : ruleDuplicateRejected
            ? null
            : sourceDocumentTitle;
        const duplicateError = protocolFile.uploaded.duplicateSkipped
          ? this.i18n.tr('documents.form.duplicateFileSkippedWarning')
          : null;
        await this.recordImportProtocolEntry({
          originalFileName: protocolFile.file.name,
          startedAt: protocolFile.startedAt,
          completedAt: new Date().toISOString(),
          documentFileId: protocolFile.uploaded.id,
          sourceDocumentId,
          sourceDocumentTitle,
          currentDocumentId,
          currentDocumentTitle,
          targetDocumentId: moved ? ruleResponse?.targetDocumentId : null,
          targetDocumentTitle: moved ? ruleResponse?.documentTitle : null,
          appliedRuleTitle: ruleResponse?.appliedRuleTitle ?? null,
          outcome,
          isDuplicate: protocolFile.uploaded.isDuplicate === true ||
            ruleResponse?.duplicateSkipped === true,
          error: duplicateError ?? ruleError ?? ruleResponse?.error ?? null,
        });
      }
      protocolEntriesRecorded = true;
      if (ruleResponse?.duplicateSkipped === true) {
        duplicateResults.length = 0;
        for (const protocolFile of protocolFiles) {
          duplicateResults.push({
            fileName: protocolFile.uploaded.fileName,
            status: 'rejected',
            statusText: this.i18n.tr(
              'documents.form.duplicateFileRejectedStatus',
            ),
            detail: this.i18n.tr(
              'documents.form.duplicateFileRejectedByRule',
            ),
          });
        }
      }
      if (duplicateResults.length > 0) {
        await ConfirmDialog.alertDuplicateResults(
          duplicateResults,
          this.i18n.tr('documents.form.duplicateFileTitle'),
          this.i18n.tr('documents.form.duplicateFileSummary'),
        );
      }
      if (
        ruleResponse?.duplicateSkipped === true &&
        !this.isEditMode &&
        ruleResponse.sourceDocumentDeleted !== true
      ) {
        await deleteDocument(sourceDocumentId);
        sourceDocumentDeletedByClient = true;
      }
      if (sourceDocumentDeletedByClient) {
        await this.finishOwnedImportProtocol();
        this.saved();
        return;
      }

      const originalUsernames = new Set(this._originalSharedWith.map(user => user.username.toLowerCase()));
      const currentUsernames = new Set(this.sharedWith.map(user => user.username.toLowerCase()));
      const usernamesToAdd = this.sharedWith
        .filter(user => !originalUsernames.has(user.username.toLowerCase()))
        .map(user => user.username);
      const usernamesToRemove = this._originalSharedWith
        .filter(user => !currentUsernames.has(user.username.toLowerCase()))
        .map(user => user.username);

      // Warn if removing a user who has follow-ups on this document
      if (usernamesToRemove.length > 0 && this.documentToEdit) {
        const followUpsLost = usernamesToRemove.filter(u =>
          (this.documentToEdit!.followUps ?? []).some(fu => fu.forUser?.toLowerCase() === u.toLowerCase())
        );
        if (followUpsLost.length > 0) {
          const names = followUpsLost.map(u => {
            const user = this._originalSharedWith.find(s => s.username.toLowerCase() === u.toLowerCase());
            return user?.displayName ?? u;
          }).join(', ');
          const confirmed = await ConfirmDialog.confirm(
            this.i18n.tr('sharing.removeFollowUpsWarning', { names }),
            this.i18n.tr('sharing.removeConfirmTitle')
          );
          if (!confirmed) return;
        }
      }

      if (usernamesToAdd.length > 0) {
        await shareDocuments([docId], usernamesToAdd);
      }
      for (const username of usernamesToRemove) {
        await unshareDocument(docId, username);
      }

      for (const fu of this.pendingFollowUps) {
          await addFollowUp(
            docId,
            new Date(fu.dueDate).toISOString(),
            fu.note || undefined,
            fu.forUser || undefined,
          );
      }

      // Clear the incoming flag if the user toggled "Mark as reviewed"
      if (this.isIncoming && this.pendingMarkAsReviewed && docId) {
        await clearIncomingFlag(docId);
      }

      await this.finishOwnedImportProtocol();
      this.saved();
    } catch (err: unknown) {
      if (!protocolEntriesRecorded && this.importProtocolNumber !== null) {
        const sourceDocumentTitle = this.title.trim() || effectiveTitle;
        const fallbackError = err instanceof Error ? err.message : String(err);
        for (const protocolFile of protocolFiles) {
          const duplicateRejected = protocolFile.uploaded.duplicateSkipped === true;
          await this.recordImportProtocolEntry({
            originalFileName: protocolFile.file.name,
            startedAt: protocolFile.startedAt,
            completedAt: new Date().toISOString(),
            documentFileId: protocolFile.uploaded.id,
            sourceDocumentId: createdDocumentId,
            sourceDocumentTitle,
            currentDocumentId: createdDocumentId,
            currentDocumentTitle: sourceDocumentTitle,
            outcome: duplicateRejected ? 'rejected' : 'added',
            isDuplicate: protocolFile.uploaded.isDuplicate === true,
            error: duplicateRejected
              ? this.i18n.tr('documents.form.duplicateFileSkippedWarning')
              : null,
          });
        }
        if (failedUpload) {
          const failedIndex = filesToUpload.indexOf(failedUpload.file);
          for (const file of filesToUpload.slice(failedIndex + 1)) {
            await this.recordImportProtocolEntry({
              originalFileName: file.name,
              startedAt: new Date().toISOString(),
              completedAt: new Date().toISOString(),
              sourceDocumentId: createdDocumentId,
              sourceDocumentTitle,
              currentDocumentId: createdDocumentId,
              currentDocumentTitle: sourceDocumentTitle,
              outcome: 'error',
              isDuplicate: false,
              error: this.i18n.tr('documents.form.importNotAttempted'),
            });
          }
          await this.recordImportProtocolEntry({
            originalFileName: failedUpload.file.name,
            startedAt: failedUpload.startedAt,
            completedAt: new Date().toISOString(),
            sourceDocumentId: createdDocumentId,
            sourceDocumentTitle,
            currentDocumentId: createdDocumentId,
            currentDocumentTitle: sourceDocumentTitle,
            outcome: 'error',
            isDuplicate: false,
            error: failedUpload.error,
          });
        } else {
          for (const file of filesToUpload) {
            if (protocolFiles.some(protocolFile => protocolFile.file === file)) continue;
            await this.recordImportProtocolEntry({
              originalFileName: file.name,
              startedAt: new Date().toISOString(),
              completedAt: new Date().toISOString(),
              sourceDocumentId: createdDocumentId,
              sourceDocumentTitle,
              currentDocumentId: createdDocumentId,
              currentDocumentTitle: sourceDocumentTitle,
              outcome: 'error',
              isDuplicate: false,
              error: fallbackError,
            });
          }
        }
        protocolEntriesRecorded = true;
      }
      this.error = err instanceof Error ? err.message : this.i18n.tr('documents.form.saveFailed');
    } finally {
      this.saving = false;
    }
  }

  private async recordImportProtocolEntry(
    entry: Parameters<typeof addImportProtocolEntry>[1],
  ): Promise<void> {
    if (this.importProtocolNumber === null) return;
    try {
      await addImportProtocolEntry(this.importProtocolNumber, entry);
    } catch (error: unknown) {
      console.error('[Radish DMS] Could not record import protocol entry', {
        protocolNumber: this.importProtocolNumber,
        fileName: entry.originalFileName,
        error,
      });
    }

  }

  private async ensureImportProtocol(): Promise<boolean> {
    if (this.importProtocolNumber !== null) return true;
    try {
      const protocol = await createImportProtocol();
      if (!Number.isSafeInteger(protocol.number) || protocol.number <= 0) {
        throw new Error(this.i18n.tr('importProtocols.invalidNumber'));
      }
      this.importProtocolNumber = protocol.number;
      this.ownsImportProtocol = true;
      return true;
    } catch (error: unknown) {
      this.error = error instanceof Error
        ? error.message
        : this.i18n.tr('documents.dropZone.createError');
      return false;
    }
  }

  private async finishOwnedImportProtocol(): Promise<void> {
    if (!this.ownsImportProtocol || this.importProtocolNumber === null) return;
    const number = this.importProtocolNumber;
    this.ownsImportProtocol = false;
    this.importProtocolNumber = null;
    try {
      await completeImportProtocol(number);
    } catch (error: unknown) {
      console.error('[Radish DMS] Could not complete import protocol', {
        protocolNumber: number,
        error,
      });
    }
  }

  private async removeOrphanedFormTags(): Promise<void> {
    const removedFormFiles = this.existingFiles.filter(file =>
      this.filesToRemove.includes(file.id) && isFormSubmissionFileShared(file));
    if (removedFormFiles.length === 0) return;

    let forms: FormDefinition[];
    try {
      forms = await listForms();
    } catch {
      throw new Error(this.i18n.tr('documents.form.formFile.tagRemovalCheckFailed'));
    }

    const formsByTitle = new Map(
      forms.map(form => [form.title.toLocaleLowerCase(), form]),
    );
    const removedTags = new Map<string, string>();
    const remainingTags = new Set<string>();
    let hasUnknownRemainingForm = false;

    for (const file of removedFormFiles) {
      const autoTags = await this.autoTagsForFormFile(file, formsByTitle);
      if (!autoTags) continue;
      for (const tagName of autoTags) {
        removedTags.set(tagName.toLocaleLowerCase(), tagName);
      }
    }

    for (const file of this.existingFiles) {
      if (this.filesToRemove.includes(file.id) || !isFormSubmissionFileShared(file)) continue;
      const autoTags = await this.autoTagsForFormFile(file, formsByTitle);
      if (!autoTags) {
        hasUnknownRemainingForm = true;
        continue;
      }
      for (const tagName of autoTags) {
        remainingTags.add(tagName.toLocaleLowerCase());
      }
    }

    if (hasUnknownRemainingForm) return;

    for (const [tagKey, tagName] of removedTags) {
      if (remainingTags.has(tagKey)) continue;

      const selectedTag = this.selectedTags.find(tag =>
        tag.name.toLocaleLowerCase() === tagKey);
      if (!selectedTag) continue;

      const confirmed = await ConfirmDialog.confirm(
        this.i18n.tr('documents.form.formFile.removeAutoTagConfirm', { tag: tagName }),
        this.i18n.tr('documents.form.formFile.removeAutoTagTitle'),
      );
      if (confirmed) {
        this.selectedTags = this.selectedTags.filter(tag =>
          tag.name.toLocaleLowerCase() !== tagKey);
      }
    }
  }

  private async autoTagsForFormFile(
    file: DocumentFile,
    formsByTitle: Map<string, FormDefinition>,
  ): Promise<string[] | null> {
    try {
      const text = await readDocumentFileText(this.documentToEdit!.id, file.id);
      const submission = parseFormSubmission(
        text,
        this.i18n.tr('documents.form.formFile.invalid'),
      );
      const form = formsByTitle.get(submission.title.toLocaleLowerCase());
      return form ? autoTagNamesForFormData(form, submission.data) : null;
    } catch {
      throw new Error(this.i18n.tr('documents.form.formFile.tagRemovalCheckFailed'));
    }
  }

  public async cancel(): Promise<void> {
    if (this.ocrRunningCount > 0) return;
    if (this.hasChanges) {
      const discard = await ConfirmDialog.confirm(this.i18n.tr('documents.form.unsavedChanges'), this.i18n.tr('documents.form.unsavedChangesTitle'));
      if (!discard) return;
    }
    await this.finishOwnedImportProtocol();
    this.closed();
  }

  public async deleteDocument(): Promise<void> {
    if (!this.documentToEdit || this.saving || this.deleting || this.ocrRunningCount > 0) return;

    const message = this.i18n.tr('documents.delete.confirmOne', { title: this.documentToEdit.title ?? '' });
    const confirmed = await ConfirmDialog.confirm(message, this.i18n.tr('documents.delete.title'));
    if (!confirmed) return;

    this.deleting = true;
    this.error = '';
    try {
      await deleteDocument(this.documentToEdit.id);
      await this.finishOwnedImportProtocol();
      if (this.deleted) this.deleted(); else this.closed();
    } catch (err: unknown) {
      this.error = err instanceof Error ? err.message : this.i18n.tr('documents.delete.failed');
    } finally {
      this.deleting = false;
    }
  }

  public toggleMarkAsReviewed(): void {
    this.pendingMarkAsReviewed = !this.pendingMarkAsReviewed;
  }

  // ── Move / merge files into another document ─────────────────────────────

  public get movableFiles(): DocumentFile[] {
    return this.existingFiles.filter(f => !this.filesToRemove.includes(f.id));
  }

  /** Move a single file elsewhere (e.g. a mis-scanned/mis-filed page). */
  public openMoveFileDialog(file: DocumentFile): void {
    this.moveDialogPreselectedIds = [file.id];
    this.showMoveDialog = true;
  }

  /** Move ALL of this document's files elsewhere — effectively merges this document into another one. */
  public openMergeDialog(): void {
    this.moveDialogPreselectedIds = this.movableFiles.map(f => f.id);
    this.showMoveDialog = true;
  }

  public closeMoveDialog = (): void => {
    this.showMoveDialog = false;
    this.moveDialogPreselectedIds = [];
  };

  public onFilesMoved = async (movedFileIds: string[], _targetDocumentId: string, targetDocumentTitle: string, errorMessage?: string): Promise<void> => {
    this.showMoveDialog = false;
    this.moveDialogPreselectedIds = [];

    if (movedFileIds.length > 0) {
      this.existingFiles = this.existingFiles.filter(f => !movedFileIds.includes(f.id));
      this.filesToRemove = this.filesToRemove.filter(id => !movedFileIds.includes(id));
      this._initialFileOrder = this._initialFileOrder.filter(id => !movedFileIds.includes(id));
      if (this.sideBySideFileId && movedFileIds.includes(this.sideBySideFileId)) {
        this.sideBySideFileId = this.existingFiles[0]?.id ?? null;
      }
      this.moveSuccessMessage = movedFileIds.length === 1
        ? this.i18n.tr('documents.moveFiles.movedOne', { target: targetDocumentTitle })
        : this.i18n.tr('documents.moveFiles.movedMany', { count: movedFileIds.length, target: targetDocumentTitle });
      setTimeout(() => { this.moveSuccessMessage = ''; }, 5000);
    }

    if (errorMessage) {
      this.error = this.i18n.tr('documents.moveFiles.partialFailure', { count: movedFileIds.length, message: errorMessage });
    }

    // If the document is now completely empty (no existing files left, and nothing pending
    // to upload), offer to delete it right away instead of leaving an empty husk behind.
    if (this.isEditMode && this.existingFiles.length === 0 && this.pendingFiles.length === 0 && this.documentToEdit) {
      const confirmed = await ConfirmDialog.confirm(
        this.i18n.tr('documents.moveFiles.emptyAfterMovePrompt', { title: this.documentToEdit.title ?? '' }),
        this.i18n.tr('documents.moveFiles.emptyAfterMoveTitle'));
      if (confirmed) {
        this.deleting = true;
        try {
          await deleteDocument(this.documentToEdit.id);
          if (this.deleted) this.deleted(); else this.closed();
        } catch (err: unknown) {
          this.error = err instanceof Error ? err.message : this.i18n.tr('documents.delete.failed');
        } finally {
          this.deleting = false;
        }
      }
    }
  };

  // ── Sharing (action bar) ──────────────────────────────────────────────────

  /** Edit mode: open the reusable share dialog to add shares immediately. Create mode: reveal the inline editor. */
  public openShareDialog(): void {
    if (this.isEditMode) {
      this.showShareModal = true;
    } else {
      this.showShareEditor = true;
    }
  }

  public closeShareModal = (): void => {
    this.showShareModal = false;
  };

  /** After the share dialog applies new shares, refresh our local copy so it's reflected immediately and isn't re-submitted on save. */
  public onSharesSaved = async (): Promise<void> => {
    this.showShareModal = false;
    if (this.documentToEdit) {
      try {
        const shares = await getDocumentShares(this.documentToEdit.id);
        this.sharedWith = shares.map(u => ({ ...u }));
        this._originalSharedWith = shares.map(u => ({ ...u }));
      } catch {
        // Non-fatal: local list simply won't reflect the change until reopened.
      }
    }
  };

  // ── Follow-ups (action bar) ───────────────────────────────────────────────

  public openAddFollowUp(): void {
    this.editingFollowUpId = null;
    this.editingPendingFollowUpIndex = null;
    this.newFollowUpDate = '';
    this.newFollowUpNote = '';
    this.followUpForUser = '';
    this.showFollowUpEditor = true;
  }

  public confirmAddFollowUp(): void {
    if (this.editingFollowUpId) {
      const fu = this.existingFollowUps.find(f => f.id === this.editingFollowUpId);
      if (fu) {
        fu.dueDate = this.newFollowUpDate;
        fu.note    = this.newFollowUpNote.trim() || null;
        fu.forUser = this.followUpForUser || this.currentUsername || null;
      }
    } else if (this.editingPendingFollowUpIndex !== null) {
      const index = this.editingPendingFollowUpIndex;
      this.pendingFollowUps = this.pendingFollowUps.map((fu, i) => i === index
        ? { dueDate: this.newFollowUpDate, note: this.newFollowUpNote.trim(), forUser: this.followUpForUser || null }
        : fu);
    } else {
      this.addPendingFollowUp();
    }
    this.editingFollowUpId = null;
    this.editingPendingFollowUpIndex = null;
    this.showFollowUpEditor = false;
  }

  public cancelAddFollowUp(): void {
    this.editingFollowUpId = null;
    this.editingPendingFollowUpIndex = null;
    this.newFollowUpDate = '';
    this.newFollowUpNote = '';
    this.followUpForUser = '';
    this.showFollowUpEditor = false;
  }

  // ── Preview / Lightbox (incoming mode) ───────────────────────────────────

  public previewUrl(fileId: string, uploadedAt?: string): string {
    const version = uploadedAt ? `?v=${encodeURIComponent(uploadedAt)}` : '';
    return apiUrl(`/documents/${this.documentToEdit!.id}/files/${fileId}/preview${version}`);
  }

  public downloadUrl(fileId: string): string {
    return apiUrl(`/documents/${this.documentToEdit!.id}/files/${fileId}/download`);
  }

  public openFileInNewTab(fileId: string): void {
    window.open(this.downloadUrl(fileId), '_blank', 'noopener,noreferrer');
  }

  public openExtractedTextInNewWindow(fileId: string): void {
    const documentId = this.documentToEdit?.id;
    if (!documentId) return;
    const textUrl = apiUrl(`/documents/${documentId}/files/${fileId}/text`);
    window.open(textUrl, '_blank', 'noopener,noreferrer,width=1000,height=800,resizable=yes,scrollbars=yes');
  }

  public openLightbox(file: DocumentFile, index: number): void {
    this.lightboxFile = file;
    this.lightboxIndex = index;
  }

  public closeLightbox(): void {
    this.lightboxFile = null;
  }

  public lightboxPrev(): void {
    const files = this.existingFiles;
    if (files.length <= 1) return;
    this.lightboxIndex = (this.lightboxIndex - 1 + files.length) % files.length;
    this.lightboxFile = files[this.lightboxIndex];
  }

  public lightboxNext(): void {
    const files = this.existingFiles;
    if (files.length <= 1) return;
    this.lightboxIndex = (this.lightboxIndex + 1) % files.length;
    this.lightboxFile = files[this.lightboxIndex];
  }

  // ── Side-by-side edit/view mode ──────────────────────────────────────────

  public toggleSideBySide(): void {
    this.sideBySideMode = !this.sideBySideMode;
    if (this.sideBySideMode && !this.sideBySideFileId && this.existingFiles.length > 0) {
      this.sideBySideFileId = this.existingFiles[0].id;
    }
  }

  public selectSideBySideFile(file: DocumentFile): void {
    this.sideBySideFileId = file.id;
  }

  public sideBySidePrev(): void {
    const files = this.existingFiles;
    if (files.length <= 1) return;
    const idx = files.findIndex(f => f.id === this.sideBySideFileId);
    const newIdx = (idx <= 0 ? files.length : idx) - 1;
    this.sideBySideFileId = files[newIdx].id;
  }

  public sideBySideNext(): void {
    const files = this.existingFiles;
    if (files.length <= 1) return;
    const idx = files.findIndex(f => f.id === this.sideBySideFileId);
    const newIdx = (idx + 1) % files.length;
    this.sideBySideFileId = files[newIdx].id;
  }

  public isImage(file: DocumentFile): boolean {
    const ct = (file.contentType ?? '').toLowerCase();
    const ext = file.fileName.toLowerCase();
    return ct.startsWith('image/') || /\.(jpe?g|png|gif|webp|bmp|svg|tiff?)$/i.test(ext);
  }

  public isPdf(file: DocumentFile): boolean {
    const ct = (file.contentType ?? '').toLowerCase();
    const ext = file.fileName.toLowerCase();
    return ct === 'application/pdf' || ext.endsWith('.pdf');
  }

  public hasFormPreview(file: DocumentFile | null): boolean {
    return file !== null && file.hasPreview && this.isFormSubmissionFile(file);
  }

  public isFormSubmissionFile(file: DocumentFile): boolean {
    return isFormSubmissionFileShared(file);
  }

  public formPreviewTitle(file: DocumentFile): string {
    return this.formPreviewDetails[file.id]?.formTitle
      ?? this.i18n.tr('documents.form.formFile.title');
  }

  public formPreviewUniqueKey(file: DocumentFile): string {
    return this.formPreviewDetails[file.id]?.uniqueKey ?? '';
  }

  public filePreviewTitle(file: DocumentFile): string {
    return this.isFormSubmissionFile(file) ? this.formPreviewTitle(file) : file.fileName;
  }

  private async loadFormPreviewDetails(): Promise<void> {
    const documentId = this.documentToEdit?.id;
    const loadVersion = ++this._formPreviewLoadVersion;
    this.formPreviewDetails = {};
    if (!documentId) return;

    const formFiles = this.existingFiles.filter(file =>
      this.isFormSubmissionFile(file));
    if (formFiles.length === 0) return;

    let forms: FormDefinition[] = [];
    try {
      forms = await listForms();
    } catch {
      // The stored submission still contains its title, so preview labels can
      // remain useful even when definitions cannot currently be loaded.
    }
    const formsByTitle = new Map(
      forms.map(form => [form.title.toLocaleLowerCase(), form]),
    );

    await Promise.all(formFiles.map(async file => {
      try {
        const text = await readDocumentFileText(documentId, file.id);
        const submission = parseFormSubmission(
          text,
          this.i18n.tr('documents.form.formFile.invalid'),
        );
        const form = formsByTitle.get(submission.title.toLocaleLowerCase());
        const details = form
          ? getFormPreviewDetails(form, submission.data)
          : { formTitle: submission.title, uniqueKey: '' };
        if (loadVersion === this._formPreviewLoadVersion) {
          this.formPreviewDetails = {
            ...this.formPreviewDetails,
            [file.id]: details,
          };
        }
      } catch {
        if (loadVersion === this._formPreviewLoadVersion) {
          this.formPreviewDetails = {
            ...this.formPreviewDetails,
            [file.id]: {
              formTitle: this.i18n.tr('documents.form.formFile.title'),
              uniqueKey: '',
            },
          };
        }
      }
    }));
  }

  public previewKindForExistingFile(file: DocumentFile): 'image' | 'pdf' | null {
    if (this.hasFormPreview(file)) return 'image';
    if (this.isImage(file)) return 'image';
    // PDFs: the server preview endpoint returns a JPEG thumbnail, so treat as image
    if (this.isPdf(file)) return 'image';
    return null;
  }

  private previewKindForPendingFile(file: File): 'image' | 'pdf' | null {
    const type = (file.type ?? '').toLowerCase();
    if (type.startsWith('image/') || /\.(jpe?g|png|gif|webp|bmp|svg|tiff?)$/i.test(file.name)) return 'image';
    if (type === 'application/pdf' || /\.pdf$/i.test(file.name)) return 'pdf';
    return null;
  }

  private canPreviewPendingFile(file: File): boolean {
    // Only images get a client-side blob preview; PDFs and other types show a placeholder.
    // PDF thumbnails are generated server-side after upload.
    return this.previewKindForPendingFile(file) === 'image';
  }

  public formatFileSize(bytes: number | null | undefined): string {
    return formatFileSizeShared(bytes);
  }

  private reset(): void {
    this._formPreviewLoadVersion++;
    this.formPreviewDetails = {};
    this.title                = '';
    this.description          = '';
    this.documentDate         = new Date().toISOString().substring(0, 10);
    this.securityLevel        = 1;
    this.existingFiles        = [];
    this.filesToRemove        = [];
    this.pendingFiles         = [];
    this.selectedTags         = [];
    this.sharedWith           = [];
    this._originalSharedWith  = [];
    this.existingFollowUps    = [];
    this._originalFollowUps   = [];
    this.followUpsToRemove    = [];
    this.pendingFollowUps     = [];
    this.newFollowUpDate      = '';
    this.newFollowUpNote      = '';
    this.followUpForUser      = '';
    this.editingFollowUpId    = null;
    this.editingPendingFollowUpIndex = null;
    this._initialTitle        = '';
    this._initialDescription  = '';
    this._initialDate         = '';
    this.saving               = false;
    this.error                = '';
    this.pendingMarkAsReviewed = false;
    this.lightboxFile         = null;
    this.lightboxIndex        = 0;
    this.sideBySideMode       = false;
    this.sideBySideFileId     = null;
    this.showMoveDialog       = false;
    this.moveDialogPreselectedIds = [];
    this.moveSuccessMessage   = '';
    this.showShareModal       = false;
    this.showShareEditor      = false;
    this.showFollowUpEditor   = false;
    this.activeTab            = 'details';
    this.followUpHistoryAvailable = false;
    this.followUpHistoryCount = 0;
    this.followUpHistoryLoading = false;
    this.followUpHistoryLoaded = false;
    this.followUpHistoryError = '';
    this.followUpHistory = [];
    this.expandedFollowUpHistory = {};
    this._detailsHeightPx = null;
    if (this.modalElement) this.modalElement.style.height = '';
    this._initialFileOrder    = [];
    this.dragSourceIndex      = null;
    this.dragOverIndex        = null;
    this.isFileDragOver       = false;
    this._modalDragEl         = null;
    this.shareInputRef        = null;
    this.clearPendingPreviewEntries();
  }

  private clearPendingPreviewEntries(): void {
    for (const entry of this.pendingPreviewEntries) {
      if (entry.objectUrl) {
        URL.revokeObjectURL(entry.objectUrl);
      }
    }
    this.pendingPreviewEntries = [];
  }

  private revokePendingPreview(index: number): void {
    const entry = this.pendingPreviewEntries[index];
    if (entry?.objectUrl) {
      URL.revokeObjectURL(entry.objectUrl);
    }
  }

  private async _loadCurrentUser(): Promise<void> {
    const me = await getMe().catch(() => null);
    this.currentUsername = me?.username ?? '';
  }

  private _sameUsers(left: SharedUser[], right: SharedUser[]): boolean {
    if (left.length !== right.length) return false;
    const normalize = (users: SharedUser[]) => users.map(user => user.username.toLowerCase()).sort();
    const leftNormalized = normalize(left);
    const rightNormalized = normalize(right);
    return leftNormalized.every((username, index) => username === rightNormalized[index]);
  }
}
